import { useState, useRef, useEffect } from "react"
import { SlidersHorizontal, ChevronRight, Check, X } from "lucide-react"
import { FILTER_CONFIG } from "../../constants/agenda"
import type { FilterState } from "../../types/agenda"

interface AgendaFiltersProps {
  filters: FilterState
  onFiltersChange: (filters: FilterState) => void
}

const PARTICIPATION_OPTIONS = [
  { value: "created", label: "Creati da me", color: "var(--hexa-purple)" },
  { value: "participating", label: "Partecipo", color: "#3b82f6" },
  { value: "involved", label: "Tutti i miei", color: "#22c55e" },
]

function toggleValue(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

function getFilterSummary(values: string[], getLabel: (v: string) => string): string | null {
  if (values.length === 0) return null
  if (values.length <= 2) return values.map(getLabel).join(", ")
  return `${values.length} selezionati`
}

export function AgendaFilters({ filters, onFiltersChange }: AgendaFiltersProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const activeFiltersCount = filters.eventType.length + filters.participation.length
  const hasActiveFilters = activeFiltersCount > 0

  // Click-outside handler
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setExpandedFilter(null)
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const handleResetFilters = () => {
    onFiltersChange({ eventType: [], participation: [] })
  }

  const getEventTypeLabel = (key: string) => {
    const found = FILTER_CONFIG.find((f) => f.key === key)
    return found?.label ?? key
  }

  const getParticipationLabel = (value: string) => {
    const found = PARTICIPATION_OPTIONS.find((o) => o.value === value)
    return found?.label ?? value
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className={`inline-flex items-center gap-2 rounded-xl bg-[var(--hexa-card-bg)] border px-3 py-1.5 text-sm font-medium transition ${
          hasActiveFilters
            ? "border-[var(--hexa-purple)] text-[var(--hexa-purple)]"
            : "border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] hover:border-[var(--hexa-purple)]"
        }`}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Filtri
        {hasActiveFilters && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--hexa-purple)] text-white text-xs font-medium">
            {activeFiltersCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="agenda-filters absolute top-full right-0 mt-2 w-[272px] max-w-[calc(100vw-48px)] max-h-[60dvh] overflow-y-auto bg-[var(--hexa-surface)] border border-[var(--hexa-border)] rounded-[14px] shadow-lg z-50"
          style={{ animation: "agendaFilterDropdownIn 150ms ease-out forwards" }}
        >
          <style>{`
            @keyframes agendaFilterDropdownIn {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* Tipo Evento */}
          <div className="border-b border-[var(--hexa-border-subtle)]">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpandedFilter(expandedFilter === "eventType" ? null : "eventType")}
              onKeyDown={(e) => e.key === "Enter" && setExpandedFilter(expandedFilter === "eventType" ? null : "eventType")}
              className="w-full flex items-center justify-between p-3 hover:bg-[var(--hexa-surface-elevated)] transition cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-[var(--hexa-text-primary)]">Tipo evento</span>
                {filters.eventType.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-[var(--hexa-purple-glow)] text-[var(--hexa-purple)]">
                    {getFilterSummary(filters.eventType, getEventTypeLabel)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {filters.eventType.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onFiltersChange({ ...filters, eventType: [] })
                    }}
                    className="p-0.5 hover:bg-[var(--hexa-surface)] rounded transition"
                  >
                    <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                  </button>
                )}
                <ChevronRight className={`h-4 w-4 text-[var(--hexa-text-muted)] transition-transform duration-150 ${expandedFilter === "eventType" ? "rotate-90" : ""}`} />
              </div>
            </div>
            {expandedFilter === "eventType" && (
              <div className="bg-[var(--hexa-surface-elevated)] border-t border-[var(--hexa-border-subtle)]">
                {FILTER_CONFIG.map((filter) => {
                  const isSelected = filters.eventType.includes(filter.key)
                  return (
                    <button
                      key={filter.key}
                      type="button"
                      onClick={() => onFiltersChange({ ...filters, eventType: toggleValue(filters.eventType, filter.key) })}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition ${
                        isSelected
                          ? "bg-[var(--hexa-purple-glow)] text-[var(--hexa-text-primary)]"
                          : "text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: filter.color }} />
                        {filter.label}
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-[var(--hexa-purple)]" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Partecipazione */}
          <div className={hasActiveFilters ? "border-b border-[var(--hexa-border-subtle)]" : ""}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpandedFilter(expandedFilter === "participation" ? null : "participation")}
              onKeyDown={(e) => e.key === "Enter" && setExpandedFilter(expandedFilter === "participation" ? null : "participation")}
              className="w-full flex items-center justify-between p-3 hover:bg-[var(--hexa-surface-elevated)] transition cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-[var(--hexa-text-primary)]">Partecipazione</span>
                {filters.participation.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-[var(--hexa-purple-glow)] text-[var(--hexa-purple)]">
                    {getFilterSummary(filters.participation, getParticipationLabel)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {filters.participation.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onFiltersChange({ ...filters, participation: [] })
                    }}
                    className="p-0.5 hover:bg-[var(--hexa-surface)] rounded transition"
                  >
                    <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                  </button>
                )}
                <ChevronRight className={`h-4 w-4 text-[var(--hexa-text-muted)] transition-transform duration-150 ${expandedFilter === "participation" ? "rotate-90" : ""}`} />
              </div>
            </div>
            {expandedFilter === "participation" && (
              <div className="bg-[var(--hexa-surface-elevated)] border-t border-[var(--hexa-border-subtle)]">
                {PARTICIPATION_OPTIONS.map((option) => {
                  const isSelected = filters.participation.includes(option.value)
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onFiltersChange({ ...filters, participation: toggleValue(filters.participation, option.value) })}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition ${
                        isSelected
                          ? "bg-[var(--hexa-purple-glow)] text-[var(--hexa-text-primary)]"
                          : "text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: option.color }} />
                        {option.label}
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-[var(--hexa-purple)]" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Reset All */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                handleResetFilters()
                setIsOpen(false)
              }}
              className="w-full p-3 text-[13px] text-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-glow)] transition text-center font-medium border-t border-[var(--hexa-border-subtle)]"
            >
              Azzera tutti i filtri
            </button>
          )}
        </div>
      )}
    </div>
  )
}
